import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import Breadcrumb from '../../components/Breadcrumb';
import { Modal2 } from '../../components/Modal';
import { getCollections, createCollection } from '../../lib/api';
import { toast } from 'react-toastify';
import { getMainWidth, handelTitle } from '../../utils';
import CategoryCard from './DeployedCollections';
import DragDrop from "../../components/DragDrop";
import { UploadShim, uploadProxy } from '../../UploadShim';
import { sessionStorageGet } from '../../lib/window-shim';
import ManCollectionSelect from "./Form/Sections.jsx";
import {
  Opt1,
  Opt2,
  Opt3,
  Opt4,
  ManageEVM,
  DeployERC20,
  ManageDeployer
} from "./Form/Forms.jsx";
import SearchFilterBar from "../../components/SearchFilterBar";
import { useI18n } from '../../i18n/context';

function AccessDenied() {
  const { t } = useI18n();
  const content = (
    <>
      {t('containers.management.comingSoon')}
      <br /><br /><br />
    </>
  );

  return (
    <main className="main-content mt-1 border-radius-lg">
      <Breadcrumb text1={t('containers.management.breadcrumb')} text2="" />
      <Modal2 show={true} modalTitle={t('containers.management.modalTitle')} content={content} />
      <div className="vh-100">&nbsp;</div>
    </main>
  );
}

function CollectionChooserSection({ options, onSelect, onCreate }) {
  const [selectOptions, setSelectOptions] = useState([]);
  const { t } = useI18n();

  const handleChange = async (event) => {
    const value = event.target.value;
    if (value === '0' || value === 'new') {
      if (onCreate) await onCreate();
    } else {
      if (onSelect) onSelect(value);
    }
  };

  if (!options || options.length === 0) {
    return <div className="text-muted">{t('containers.management.noCollections')}</div>;
  }

  return (
    <div className="mb-3">
      <label className="form-label">{t('containers.management.selectCollection')}</label>
      <select
        className="form-select input-purple"
        onChange={handleChange}
        defaultValue=""
      >
        <option value="" disabled>{t('containers.management.chooseCollection')}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const ManagementContainer = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [userData, setUserData] = useState(null);
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [localCollectionData, setLocalCollectionData] = useState([]);
  const [filesData, setFilesData] = useState(null);
  const [filesLen, setFilesLen] = useState(0);
  const [dragDropKey, setDragDropKey] = useState(0);
  const [mpItem, setMpItem] = useState("def");
  const [cInfo, setCInfo] = useState({});
  const [signalUpdate, setSignalUpdate] = useState(0);
  const [bcText, setBcText] = useState("");
  const [collectionData, setCollectionData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const tokenRaw = sessionStorageGet('token');
      if (!tokenRaw) {
        toast(t('containers.management.loginRequired'));
        navigate({ to: "/signin" });
        return;
      }

      try {
        const parsed = JSON.parse(tokenRaw);
        const bmp = parsed.account?.bmp?.toString(2) || "00000000";
        const canDeploy = bmp[7] === "1";

        setUserData(parsed);
        setToken(tokenRaw);
        setHasPermission(canDeploy);

        if (canDeploy) {
          loadCollections();
        }
      } catch (err) {
        console.error('Auth error:', err);
      }
    };

    checkAuth();
    getMainWidth();
    handelTitle(t('containers.management.title'));
  }, []);

  const loadCollections = async () => {
    try {
      const result = await getCollections({ byUser: -1 });
      if (result?.data) {
        setLocalCollectionData(result.data);
        const options = result.data.map(value => ({
          value: value.id,
          label: value.name,
          meta: value
        }));
        setCollectionOptions(options);
      }
    } catch (error) {
      console.error('Failed to load collections:', error);
    }
  };

  const handleCreateCollection = async (name) => {
    try {
      const result = await createCollection(name);
      toast.success(result.message || `${t('containers.management.collectionCreatedPrefix')}"${name}"${t('containers.management.collectionCreatedSuffix')}`);
      await loadCollections();
    } catch (error) {
      const msg = error?.message || error.toString();
      toast.error(`${t('containers.management.createFailedPrefix')} ${msg}`);
    }
  };

  const handleCollectionSelect = (collectionId) => {
    const coll = localCollectionData.find(c => c.id === collectionId);
    setSelectedCollection(coll);
    setCollectionData(coll || {});
    setSignalUpdate(prev => prev + 1);
  };

  const handleFilesUpdate = (files) => {
    setFilesData(files);
    setFilesLen(files ? files.length : 0);

    if (files && files.length > 0) {
      setShowModal(true);
      setModalContent(
        <div>
          <h6>{t('containers.management.readyUploadPrefix').replace('{files.length}', String(files.length))}</h6>
          <p>{t('containers.management.readyUploadSuffix')}</p>
        </div>
      );
    }
  };

  const handleUpload = async () => {
    if (!selectedCollection || !filesData) {
      toast.error(t('containers.management.selectAndAddFiles'));
      return;
    }

    const files = filesData;
    const collID = selectedCollection.id;
    const collNA = selectedCollection.name;

    try {
      const parsedToken = token ? JSON.parse(token) : null;
      if (!parsedToken) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();

        reader.onloadend = function () {
          const fdd = {
            descr: `${collNA}: ${file.name}`,
            filen: file.name,
            cid: collID,
            tid: i,
            colln: collNA,
            auth: '',
            wall: parsedToken.account.id,
            _token: parsedToken,
            nft_contract: "",
            nft_currentOwner: "0x00",
            isCached: false
          };

          const formData = new FormData();
          formData.append('json', JSON.stringify(fdd));
          formData.append('blob', file);
          uploadProxy.push(formData);
        };

        reader.readAsBinaryString(file);
      }

      toast.success(`${t('containers.management.uploadingPrefix').replace('{files.length}', String(files.length))}"${collNA}"...`);
      setFilesData(null);
      setFilesLen(0);
      setDragDropKey(prev => prev + 1);
    } catch (error) {
      toast.error(t('containers.management.uploadFailed'));
      console.error(error);
    }
  };

  const handleCreateNFT = () => {
    navigate({
      to: '/manage/deploy',
      state: { payload: [selectedCollection, filesData] }
    });
  };

  if (!hasPermission) {
    return <AccessDenied />;
  }

  return (
    <main className="main-content mt-1 border-radius-lg">
      <Modal2 show={showModal} content={modalContent} onClose={() => setShowModal(false)} />
      <Breadcrumb text1={t('containers.management.breadcrumb')} text2={bcText} />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{
          display: "flex",
          flexDirection: "row-reverse",
          flexWrap: "wrap",
          maxWidth: "1400px",
          margin: "0"
        }} className="container-fluid">

          <div className="m-1" style={{ minWidth: "30%", flexGrow: 1 }}>
            <CategoryCard
              className="card-purple"
              data1={[]}
              data2={[]}
              signalUpdate={signalUpdate}
              cData={selectedCollection || {}}
              flen={filesLen}
              goToCollection={(id) => navigate({ to: `/collection/${id}` })}
              clickCallback={() => {}}
              setCInfo={setCInfo}
              ulButton={
                <button
                  style={{ borderRadius: "10px" }}
                  onClick={handleUpload}
                  disabled={!filesData}
                >
                  {t('containers.management.uploadButton')}
                </button>
              }
              deButton={
                <div style={{ marginTop: "0.2em" }}>
                  <button
                    style={{ borderRadius: "10px" }}
                    onClick={handleCreateNFT}
                    disabled={!selectedCollection || !filesData}
                  >
                    {t('containers.management.createNftButton')}
                  </button>
                </div>
              }
            />

            <div className="card card-purple p-2 mt-2">
              <h6>{t('containers.management.collectionsHeading')}</h6>
              <CollectionChooserSection
                options={collectionOptions}
                onSelect={handleCollectionSelect}
                onCreate={(name) => {
                  const newName = prompt(t('containers.management.promptCollectionName'));
                  if (newName) handleCreateCollection(newName);
                }}
              />
            </div>
          </div>

          <div className="m-1" style={{ minWidth: "68%", flex: 1, alignSelf: "center" }}>
            {mpItem === "def" ? (
              <ManCollectionSelect
                img={null}
                text={t('containers.management.selectPlaceholder')}
                title={t('containers.management.collectionManagement')}
                clickCallback={(type) => setMpItem(type)}
              />
            ) : (
              <>
                {mpItem === "opt1" && <Opt1 clickCallback={setMpItem} />}
                {mpItem === "opt2" && <Opt2 clickCallback={setMpItem} />}
                {mpItem === "opt3" && <Opt3 clickCallback={setMpItem} ssu={setSignalUpdate} />}
                {mpItem === "opt4" && <Opt4 clickCallback={setMpItem} />}
                {mpItem === "manageEVM" && <ManageEVM cInfo={cInfo} ssu={setSignalUpdate} clickCallback={setMpItem} />}
                {mpItem === "ManageDeployer" && <ManageDeployer cInfo={cInfo} ssu={setSignalUpdate} clickCallback={setMpItem} />}
                {mpItem === "DeployERC20" && <DeployERC20 cInfo={cInfo} ssu={setSignalUpdate} clickCallback={setMpItem} />}

                <div style={{ visibility: "hidden", height: 0 }}>
                  <div key={dragDropKey}>
                    <DragDrop
                      del={false}
                      multiple={true}
                      delAll={() => {
                        setFilesData(null);
                        setFilesLen(0);
                      }}
                      updateFilesCb={handleFilesUpdate}
                    />
                  </div>
                </div>

                <div className="container-fluid mt-3" style={{ display: 'none' }}>
                  <article>
                    <output id="result" />
                  </article>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ManagementContainer;
